import React from 'react'
import { Option } from '../../../types/database'
import { OptionLeadersList } from '../../aircraft/OptionLeadersList'
import InlineOptionsEditor from '../../inline-editors/InlineOptionsEditor'
import { CellBaseProps } from './types'

interface OptionsCellProps extends CellBaseProps {
  options: Map<number, Option[]>
  transactionOptions: Map<string, Option>
  editingOptionsAircraftId: number | null
  setEditingOptionsAircraftId: (id: number | null) => void
  handleOptionsSave: (aircraftId: number, newOptions: string[]) => Promise<void>
  handleCancelOptionsEdit: () => void
  handleTransactionOptionsSave: (transactionId: string, newOptions: string[]) => Promise<void>
}

/**
 * Renders the Options column cell. Handles both sold (transaction-based)
 * and for-sale/removed (aircraft-based) options display and editing.
 */
export function OptionsCell(props: OptionsCellProps) {
  const {
    aircraft, tableType, isEditMode, cellBorderClass, aircraftTransaction,
    transactionOverrides, options, transactionOptions,
    editingOptionsAircraftId, setEditingOptionsAircraftId,
    handleOptionsSave, handleCancelOptionsEdit, handleTransactionOptionsSave
  } = props

  const isEditingOptions = editingOptionsAircraftId === aircraft.aircraft_id

  if (tableType === 'sold' && aircraftTransaction) {
    const transactionOverride = transactionOverrides.get(aircraftTransaction.transaction_id)
    const optionIds = transactionOverride?.options || aircraftTransaction.options || []

    const resolvedOptions: Option[] = optionIds
      .map((optionId: string) => transactionOptions.get(optionId))
      .filter((opt: Option | undefined): opt is Option => opt !== undefined)

    return (
      <td className={`px-2 py-2 text-sm text-gray-900 text-left ${cellBorderClass} relative overflow-hidden`}>
        <div
          className={isEditMode ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1 min-h-[1.5rem]' : ''}
          onClick={isEditMode ? (e) => { e.stopPropagation(); setEditingOptionsAircraftId(aircraft.aircraft_id); } : undefined}
        >
          {isEditingOptions && aircraft.model_id ? (
            <InlineOptionsEditor
              aircraftId={aircraft.aircraft_id}
              modelId={aircraft.model_id}
              currentOptions={optionIds}
              onSave={(newOptions) => handleTransactionOptionsSave(aircraftTransaction.transaction_id, newOptions)}
              onCancel={handleCancelOptionsEdit}
            />
          ) : (
            resolvedOptions.length > 0
              ? <OptionLeadersList options={resolvedOptions} />
              : optionIds.length > 0
                ? optionIds.map((optionId: string, idx: number) => (
                    <div key={idx} className="truncate text-gray-500">
                      {optionId}
                    </div>
                  ))
                : '-'
          )}
        </div>
      </td>
    )
  }

  // For For Sale and Removed aircraft
  const aircraftOptions = options.get(aircraft.aircraft_id) || []
  const currentOptionIds = aircraftOptions.map(o => o.option_id)

  return (
    <td className={`px-2 py-2 text-sm text-gray-900 text-left ${cellBorderClass} relative overflow-hidden`}>
      <div
        className={isEditMode ? 'cursor-pointer hover:bg-green-50 rounded px-1 -mx-1 min-h-[1.5rem]' : ''}
        onClick={isEditMode ? (e) => { e.stopPropagation(); setEditingOptionsAircraftId(aircraft.aircraft_id); } : undefined}
      >
        {isEditingOptions && aircraft.model_id ? (
          <InlineOptionsEditor
            aircraftId={aircraft.aircraft_id}
            modelId={aircraft.model_id}
            currentOptions={currentOptionIds}
            onSave={(newOptions) => handleOptionsSave(aircraft.aircraft_id, newOptions)}
            onCancel={handleCancelOptionsEdit}
          />
        ) : (
          aircraftOptions.length > 0
            ? <OptionLeadersList options={aircraftOptions} />
            : '-'
        )}
      </div>
    </td>
  )
}

